import * as React from "react";
import { Skeleton } from "../skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { SectionHeading } from "./section-heading";

/**
 * Canonical skeleton patterns for the Excellent monorepo.
 *
 * Every loading state should match one of these shapes. If a new shape is
 * needed, add it here first — agents review this file as the source of truth.
 *
 * Sizing rules:
 *   - type-body line  →  h-4 (16px)
 *   - type-heading  →  h-6 for 18/24 sub-headings; page-title → h-8 for 28/32
 *   - input/select  →  h-8 (matches rounded-md border px-3 py-1.5 type-body)
 *   - textarea x4   →  h-24
 *   - button        →  h-8 w-<content>
 *   - card          →  h-32 rounded-lg for shallow cards, h-72 for charts
 *
 * Two colors only: bg-accent (skeleton default) on bg-background.
 */
export function SkeletonExamples() {
  return (
    <div className="flex flex-col gap-10 p-6">
      <Example title="Page header" description="Title + subtitle + primary action">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
          </div>
          <Skeleton className="h-8 w-32" />
        </div>
      </Example>

      <Example title="Paragraph" description="Multi-line text content">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </Example>

      <Example title="Avatar + label row" description="List item with circular avatar and two text lines">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
      </Example>

      <Example title="Stat card grid" description="Four stat cards (matches StatCard height)">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[104px] rounded-lg" />
          ))}
        </div>
      </Example>

      <Example title="Card" description="Shallow card body (header rendered, body loading)">
        <Card>
          <CardHeader>
            <CardTitle>
              <Skeleton className="h-5 w-32" />
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-2/3" />
          </CardContent>
        </Card>
      </Example>

      <Example title="Chart card" description="Tall placeholder for a chart or graph">
        <Skeleton className="h-72 rounded-lg" />
      </Example>

      <Example title="Form field" description="Label + input — matches InlineFieldText">
        <div className="grid grid-cols-2 gap-3">
          <FormFieldSkeleton />
          <FormFieldSkeleton />
        </div>
      </Example>

      <Example title="Form field — textarea" description="Label + 4-row textarea">
        <FormFieldSkeleton textareaRows={4} />
      </Example>

      <Example
        title="Table"
        description="Header + 5 rows. Mirrors table style rules: column dividers + px-2 cells. Skeleton cells fill columns identically to loaded rows."
      >
        <div className="overflow-hidden rounded-md border border-border">
          {/* table-ignore: loading-skeleton placeholder, not a data table — no
              rows/columns to model; deliberately mirrors the atom's divider +
              px-2 chrome so the skeleton matches the loaded <DataTable>. */}
          <table className="w-full border-collapse type-body">
            <thead>
              <tr className="border-b border-border bg-background/40">
                {["w-1/3", "w-1/4", "w-1/6", "w-1/6"].map((w, i) => (
                  <th
                    key={i}
                    className="border-r border-border/40 px-2 py-2 text-left last:border-r-0"
                  >
                    <Skeleton className={`h-4 ${w}`} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, r) => (
                <tr key={r} className="border-b border-border/30 last:border-b-0">
                  {["w-2/3", "w-1/2", "w-1/3", "w-1/4"].map((w, c) => (
                    <td
                      key={c}
                      className="border-r border-border/40 px-2 py-2 last:border-r-0"
                    >
                      <Skeleton className={`h-4 ${w}`} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Example>

      <Example title="List rows" description="Bordered rows (e.g. AutoVerifyHistory)">
        <ul className="flex flex-col gap-1">
          {Array.from({ length: 4 }).map((_, i) => (
            <li key={i} className="rounded-md border border-border px-3 py-2">
              <Skeleton className="h-4 w-3/4" />
            </li>
          ))}
        </ul>
      </Example>

      <Example title="Button" description="Single button placeholder (h-8 w-content)">
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-32" />
        </div>
      </Example>
    </div>
  );
}

function FormFieldSkeleton({ textareaRows }: { textareaRows?: number }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Skeleton className="h-4 w-16" />
      <Skeleton className={textareaRows ? "h-24 w-full" : "h-8 w-full"} />
    </div>
  );
}

function Example({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <SectionHeading title={title} description={description} />
      <div className="rounded-md border border-border bg-background p-4">
        {children}
      </div>
    </section>
  );
}
